import { ArdoqId } from '@ardoq/api-types';
import {
  CollapsibleGraphGroup,
  getReferenceIdsFromReferenceList,
  getUrlFieldValuesByComponentId,
  getUrlFieldValuesByReferenceId,
  GraphEdgeBase,
  GraphItem,
  GraphNode,
} from '@ardoq/graph';
import { GraphItemsModel } from 'tabview/graphComponent/types';
import {
  GraphItems,
  GraphItemsData,
  ViewModelFromGraphArgs,
  ViewModelFromGraphResult,
} from './types';
import { getGraphItems, getGraphItemsData } from './utils';

export const toViewModel = <T extends GraphItem>(
  items: T[]
): GraphItemsModel<T> => ({
  byId: new Map<ArdoqId, T>(items.map(item => [item.id, item])),
  add: items.map(item => item.id),
  update: [],
  remove: [],
});

type GraphItemsToViewModelArgs = Pick<
  ViewModelFromGraphArgs,
  'context' | 'showOnlyConnectedComponents'
> & {
  edges: GraphEdgeBase[];
  groupNodes: Map<string, CollapsibleGraphGroup>;
  graphNodes: Map<string, GraphNode>;
};

export const graphItemsToViewModel = ({
  edges,
  graphNodes,
  groupNodes,
  context,
  showOnlyConnectedComponents,
}: GraphItemsToViewModelArgs) => {
  const noConnectedComponents =
    showOnlyConnectedComponents && graphNodes.size + groupNodes.size === 0;

  const contextComponentIsNotConnected =
    noConnectedComponents && context.componentId;

  const contextGraphNodeReplacement =
    contextComponentIsNotConnected && GraphNode.create(context.componentId);

  const nodes: GraphItemsModel<GraphNode> = contextGraphNodeReplacement
    ? {
        add: [contextGraphNodeReplacement.id],
        update: [],
        remove: [],
        byId: new Map([
          [contextGraphNodeReplacement.id, contextGraphNodeReplacement],
        ]),
      }
    : toViewModel([...graphNodes.values()]);

  return {
    nodes,
    edges: toViewModel(edges),
    groups: toViewModel([...groupNodes.values()]),
    noConnectedComponents,
  };
};

type ToViewModelShapeArgs = GraphItems &
  Pick<
    GraphItemsData,
    | 'componentIdsOfCollapsedGroups'
    | 'edgeLabelsData'
    | 'nodeLabelsData'
    | 'rawReferences'
  > &
  Pick<
    ViewModelFromGraphArgs,
    | 'context'
    | 'showOnlyConnectedComponents'
    | 'viewInterface'
    | 'rawGraph'
    | 'expandedStateChangingGroupId'
  >;
const toViewModelShape = ({
  edges,
  rawComponents,
  graphNodes,
  rawGroups,
  groupNodes,
  componentIdsOfCollapsedGroups,
  edgeLabelsData,
  nodeLabelsData,
  rawReferences,
  rawGraph,
  context,
  showOnlyConnectedComponents,
  viewInterface,
  expandedStateChangingGroupId,
}: ToViewModelShapeArgs): ViewModelFromGraphResult => {
  const componentIds = [...rawComponents, ...rawGroups]
    .filter(({ modelId }) => modelId && viewInterface.isComponent(modelId))
    .map(({ modelId }) => modelId!);

  const urlFieldValuesByComponentId = getUrlFieldValuesByComponentId(
    viewInterface,
    componentIds
  );
  const urlFieldValuesByReferenceId = getUrlFieldValuesByReferenceId(
    viewInterface,
    getReferenceIdsFromReferenceList(rawReferences)
  );
  return {
    ...graphItemsToViewModel({
      edges,
      graphNodes,
      groupNodes,
      context,
      showOnlyConnectedComponents,
    }),
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    errors: rawGraph.errors,
    hasClones: rawGraph.hasClones,
    expandedStateChangingGroupId,
    rawGraph,
    componentIdsOfCollapsedGroups,
    nodeLabelsData,
    // edgeLabelsData is currently not in use, which might indicate a reference update bug in handleUpdate
    // But we add it to the result now for consistency and deal with the possible bug separately.
    edgeLabelsData,
  };
};

const viewModelFromGraph = (
  args: ViewModelFromGraphArgs
): ViewModelFromGraphResult => {
  const graphItemsData = getGraphItemsData(args);

  const {
    viewInterface,
    context,
    collapsedGroupIds,
    showOnlyConnectedComponents,
    expandedStateChangingGroupId,
    rawGraph,
    getGraphNodeOrGroupLabel,
    scopeDiff,
    i18nLocale,
  } = args;

  const graphItems = getGraphItems({
    ...graphItemsData,
    viewInterface,
    rawCollapsedGroupIds: collapsedGroupIds,
    getGraphNodeOrGroupLabel,
    scopeDiff,
    i18nLocale,
    contextComponentId: context.componentId,
  });

  const {
    componentIdsOfCollapsedGroups,
    edgeLabelsData,
    nodeLabelsData,
    rawReferences,
  } = graphItemsData;

  return toViewModelShape({
    ...graphItems,
    // graph Items Data
    componentIdsOfCollapsedGroups,
    edgeLabelsData,
    nodeLabelsData,
    rawReferences,
    // args
    viewInterface,
    context,
    showOnlyConnectedComponents,
    expandedStateChangingGroupId,
    rawGraph,
  });
};
export { viewModelFromGraph };
